import React, { useEffect, useRef, useState } from 'react';
import styles from './styles';
import { Stage } from '@/services/stages/stage.vm';
import { CheckCircle, KeyboardArrowDown, PanoramaFishEyeOutlined } from '@mui/icons-material';
import { ContentCard } from '@/components/dataDisplay/cards/ContentCard';
import useLoadStageContents from './useLoadStageContents';
import { useParams } from 'react-router';
import theme from '@/constants/theme';
import { Content } from '@/services/contents/content.vm';

interface Props {
  stage: Stage;
  completedContents?: number;
  nextSuggestedContentId?: Content['id'];
  autoFocus?: boolean;
}

const StageComponent: React.FC<Props> = ({ stage, completedContents, nextSuggestedContentId, autoFocus }) => {
  const items = useRef<HTMLDivElement>(null);

  const { routeId } = useParams();
  const [expandStage, setExpandStage] = useState(false);
  const { stageContents } = useLoadStageContents({ routeId, stageId: stage.id });

  const handleExpandStage = () => {
    setExpandStage((prev) => !prev);
  };
  const containerRef = React.useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (autoFocus) {
      containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setExpandStage(true);
    }
  }, [autoFocus]);

  if (!stage || !routeId) return null;
  return (
    <div style={styles.stageContainer} ref={containerRef}>
      <div style={styles.listIndicator} />
      <button style={styles.stagesItems} onClick={handleExpandStage}>
        {stageContents?.length !== 0 && completedContents === stageContents?.length ? (
          <CheckCircle style={{ color: theme.colors.success }} />
        ) : (
          <PanoramaFishEyeOutlined style={{ color: theme.colors.text.caption }} />
        )}
        <div style={styles.stageDetails}>
          <h4 style={{ fontSize: 20 }}>{stage.title}</h4>
          <p style={{ fontSize: 16 }}>
            {completedContents} / {stageContents?.length ?? 0} Contenidos Completados
          </p>
        </div>
        <KeyboardArrowDown
          style={{
            position: 'absolute',
            right: 16,
            transform: expandStage ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 300ms ease-out',
          }}
        />
      </button>
      <div
        aria-hidden={!expandStage}
        style={{
          paddingLeft: 16,
          overflow: 'hidden',
          height: expandStage ? items.current?.offsetHeight : 0,
          transition: 'height 300ms ease-out',
        }}
      >
        <div
          ref={items}
          style={{
            paddingInlineStart: 0,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 'fit-content',
            opacity: expandStage ? 1 : 0,
            transition: `opacity 200ms ease-out ${expandStage ? '0.2s' : '0s'}`,
          }}
        >
          {stageContents?.map((item, index) => (
            <ContentCard
              routeId={routeId}
              key={item.id}
              id={item.id}
              title={item.title}
              type={item.type}
              completed={item.completed}
              points={item.points}
              duration={item.duration}
              autoFocus={nextSuggestedContentId === item.id}
            />
          ))}
          {stageContents?.length === 0 && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 16,
                color: theme.colors.text.caption,
                padding: 16,
              }}
            >
              <p>No hay contenidos para esta etapa</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StageComponent;
